import { Check, CircleAlert, Dot, Upload, X } from 'lucide-react';
import { cn } from '@tinct/core-utils';
import { Button } from '@tinct/web-components';
import { RequirementCheck, useBuyerLogoDropzone } from '../hooks';

export type SelectFileStepProps = {
  /** 'error' renders the alert and pass/fail marks next to each requirement. */
  step: 'select' | 'error';
  errorMessage: string | null;
  checks: RequirementCheck[];
  requirementLabels: string[];
  versionLabel: string;
  exampleLogo: string;
  dark: boolean;
  onFile: (file: File | null) => void | Promise<void>;
};

/**
 * File-selection step: requirements card with the variant example, and the
 * drag-and-drop zone. Also renders the validation-error state (same layout
 * plus alert and per-requirement pass/fail marks).
 */
export const SelectFileStep = ({
  step,
  errorMessage,
  checks,
  requirementLabels,
  versionLabel,
  exampleLogo,
  dark,
  onFile,
}: SelectFileStepProps) => {
  const {
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleInputChange,
  } = useBuyerLogoDropzone(onFile);

  return (
    <>
      {/* Error alert */}
      {step === 'error' && errorMessage && (
        <div className="flex items-center gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4">
          <CircleAlert className="size-4 shrink-0 text-destructive" />
          <p className="text-sm leading-5 text-destructive">{errorMessage}</p>
        </div>
      )}

      {/* Requirements card */}
      <div className="flex items-center gap-3 rounded-2xl bg-secondary p-4">
        <div className="flex flex-1 flex-col gap-2 p-2">
          <p className="text-sm font-medium text-foreground">
            Version needed:
          </p>
          <ul className="flex flex-col">
            {requirementLabels.map((label, index) => {
              const failed =
                step === 'error' &&
                checks.length > 0 &&
                !checks[index]?.passed;
              return (
                <li key={label} className="flex items-center gap-1.5">
                  {step === 'error' ? (
                    failed ? (
                      <X className="size-4 shrink-0 text-destructive" />
                    ) : (
                      <Check className="size-4 shrink-0 text-emerald-600" />
                    )
                  ) : (
                    <Dot className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  <span
                    className={cn(
                      'text-sm leading-5',
                      failed ? 'text-destructive' : 'text-muted-foreground'
                    )}
                  >
                    {label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        <div
          className={cn(
            'flex h-[124px] w-[156px] shrink-0 items-center justify-center rounded-md border border-border p-6',
            dark ? 'bg-neutral-800' : 'bg-background'
          )}
        >
          <img
            src={exampleLogo}
            alt={`Example logo — ${versionLabel.toLowerCase()}`}
            className="max-h-full max-w-full object-contain"
          />
        </div>
      </div>

      {/* Dropzone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed p-10 transition-colors',
          isDragging ? 'border-primary bg-primary/5' : 'border-border'
        )}
      >
        <input
          id="buyer-logo-file-input"
          type="file"
          accept=".png,.svg,image/png,image/svg+xml"
          onChange={handleInputChange}
          className="hidden"
        />
        <div className="flex size-10 items-center justify-center rounded-md bg-card shadow-xs">
          <Upload className="size-5 text-foreground" />
        </div>
        <div className="flex flex-col items-center gap-1 text-center">
          <p className="text-base font-medium text-foreground">
            {step === 'error'
              ? 'Choose a file or drag and drop'
              : 'Drop your file here'}
          </p>
          {step === 'select' && (
            <p className="text-sm text-muted-foreground">or</p>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            document.getElementById('buyer-logo-file-input')?.click()
          }
        >
          Select file
        </Button>
      </div>
    </>
  );
};
